import React from "react";
import { TextField, Typography } from "@mui/material";
import { trakrefBlue, inputGrey, red, Grey3, Grey4 } from "../../../color_utils";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PropTypes from "prop-types";
import { trakrefFontFamily, trakrefFontFamilySemiBold } from "../../../utils";
const defaultStlyes = {
    "& label.Mui-focused": {
        color: trakrefBlue,
    },
    "& .MuiOutlinedInput-root": {
        "&.Mui-focused fieldset": {
            borderColor: trakrefBlue,
        },
    },
    "& .MuiFilledInput-root": {
        backgroundColor: inputGrey,
        "&.Mui-focused fieldset": {
            borderColor: trakrefBlue,
        },
    },
    "& .MuiFilledInput-root.Mui-disabled": {
        backgroundColor: Grey4
    },
    '& .MuiFormLabel-root': { fontFamily: trakrefFontFamily },
    "& .MuiFormLabel-root.MuiInputLabel-shrink": {
        textTransform: "uppercase",
        fontFamily: trakrefFontFamilySemiBold
    },
    "&  .MuiFormLabel-root.MuiInputLabel-root.Mui-error": {
        color: "#D51A1A !important",
        fontFamily: trakrefFontFamilySemiBold,
    },
    "& .MuiInputBase-root.MuiInput-underline.Mui-error:after": {
        borderBottom: "2px solid #D51A1A !important",
    },
    "& .MuiFilledInput-root:after": { borderBottomColor: trakrefBlue },
};

const Input = ({
    id,
    required,
    placeholder,
    label,
    value,
    onChange,
    onKeyDown,
    onFocus,
    onBlur,
    error,
    fullWidth,
    variant,
    helperText,
    secondaryHelperText,
    multiLine,
    type,
    disabled,
    rows,
    maxCharacterLength,
    sx,
    minimumNumberValue,
    maximumNumberValue,
    alwaysShowHelper,
    endAdornment,
    disableDecimal,
    inputRef,
    formHelperTextPropsSx
}) => {
    // handle only numbers type
    const handleKeyDown = (e) => {
        const exceptThisSymbols = ["e", "E", "+", "-", ...(disableDecimal ? [".", ","] : [])];
        if (type === "number" && exceptThisSymbols.includes(e.key)) {
            e.preventDefault();
        }
        onKeyDown && onKeyDown(e);
    };

    return (
        <TextField
            id={id}
            required={required}
            disabled={disabled}
            onWheel={(e) => e.target.blur()}
            InputProps={{
                notched: !!value || value === 0,
                inputProps: {
                    maxLength: maxCharacterLength,
                    min: minimumNumberValue,
                    max: maximumNumberValue,
                    sx: {
                        "&::placeholder": {
                            color: Grey3,
                            opacity: 1,
                            fontSize: '16px',
                        },
                        fontFamily: trakrefFontFamily
                    },
                },
                endAdornment: endAdornment ? endAdornment : (error && <ErrorOutlineIcon sx={{ color: red }} />),
            }}
            InputLabelProps={{
                shrink: !!value || value === 0 || placeholder,
                sx: {
                    fontFamily: trakrefFontFamilySemiBold
                }
            }}
            label={label}
            placeholder={placeholder}
            value={value}
            defaultValue={value}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            onFocus={onFocus}
            onBlur={onBlur}
            error={error}
            rows={rows}
            fullWidth={fullWidth}
            variant={variant}
            helperText={
                <>
                    {secondaryHelperText &&
                        <Typography color={Grey3} fontFamily={trakrefFontFamily} fontSize={"10px"}>{secondaryHelperText}</Typography>
                    }
                    {error || alwaysShowHelper ? helperText : ""}
                </>
            }
            FormHelperTextProps={alwaysShowHelper && {
                sx: {
                    padding: 0,
                    marginRight: 0,
                    textAlign: 'end',
                    letterSpacing: '0px',
                    color: error ? red : Grey3,
                    fontFamily: trakrefFontFamily,
                    ...formHelperTextPropsSx
                }
            }}
            multiline={multiLine}
            type={type}
            sx={{ ...defaultStlyes, ...sx }}
            inputRef={inputRef}
        />
    );
}

Input.propTypes = {
    required: PropTypes.bool,
    label: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    value: PropTypes.string.isRequired,
    onChange: PropTypes.func.isRequired,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onKeyDown: PropTypes.func,
    error: PropTypes.bool,
    fullWidth: PropTypes.bool,
    variant: PropTypes.string,
    helperText: PropTypes.string,
    multiLine: PropTypes.bool,
    numberInput: PropTypes.string,
    disabled: PropTypes.bool,
    maxCharacterLength: PropTypes.number,
    id: PropTypes.string,
    sx: PropTypes.object,
    minimumNumberValue: PropTypes.number,
    maximumNumberValue: PropTypes.number,
    endAdornment: PropTypes.element,
    disableDecimal: PropTypes.bool,
    inputRef: PropTypes.func,
    formHelperTextPropsSx: PropTypes.object,
};

Input.defaultProps = {
    placeholder: "",
    required: false,
    disabled: false,
    error: false,
    fullWidth: false,
    variant: "outlined",
    helperText: "This field is required",
    multiLine: false,
    numberInput: "",
    onFocus: () => { },
    onBlur: () => { },
    onKeyDown: () => { },
    maxCharacterLength: 50000,
    id: "",
    sx: {},
    minimumNumberValue: 0,
    maximumNumberValue: 10000000000,
    endAdornment: null,
    disableDecimal: false,
    formHelperTextPropsSx: {}
};

export default Input;
